import { Clock, ShieldCheck, ShieldX } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'

interface MfaApprovalOverlayProps {
  challengeId: string
  expiresAt: string
  onResolved: () => void
}

export function MfaApprovalOverlay({
  challengeId,
  expiresAt,
  onResolved,
}: MfaApprovalOverlayProps) {
  const totalSeconds = useRef(
    Math.max(1, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)),
  )
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds.current)
  const [responding, setResponding] = useState(false)
  const [result, setResult] = useState<'approved' | 'denied' | null>(null)
  const [respondError, setRespondError] = useState<string | null>(null)

  // Countdown timer
  useEffect(() => {
    const expiry = new Date(expiresAt).getTime()
    const tick = setInterval(() => {
      const remaining = Math.max(0, Math.round((expiry - Date.now()) / 1000))
      setSecondsLeft(remaining)
      if (remaining === 0) {
        clearInterval(tick)
        fetch(`api/mfa/deny/${challengeId}`, { method: 'POST' }).catch(() => {})
        onResolved()
      }
    }, 1000)
    return () => clearInterval(tick)
  }, [expiresAt, challengeId, onResolved])

  const respond = async (action: 'approve' | 'deny') => {
    setResponding(true)
    setRespondError(null)
    try {
      const res = await fetch(`api/mfa/${action}/${challengeId}`, { method: 'POST' })
      if (!res.ok) {
        setRespondError('Could not process your response. Please try again or sign in again.')
        setResponding(false)
        return
      }
      setResult(action === 'approve' ? 'approved' : 'denied')
      setTimeout(onResolved, 1500)
    } catch {
      setRespondError('Network error. Please check your connection and try again.')
      setResponding(false)
    }
  }

  const urgentColour = secondsLeft <= 10 ? 'text-red-500' : 'text-muted-foreground'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs px-6">
      <div className="w-full max-w-sm bg-card rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-linear-to-r from-primary to-primary/80 px-6 py-5 text-center">
          <div className="flex justify-center mb-2">
            <ShieldCheck className="w-10 h-10 text-primary-foreground/90" />
          </div>
          <h2 className="text-lg font-bold text-primary-foreground">Login Approval Request</h2>
          <p className="text-xs text-primary-foreground/70 mt-1">
            Someone is attempting to sign in to your REINZ account
          </p>
        </div>

        <div className="px-6 py-5">
          {result === null && (
            <>
              <p className="text-sm text-foreground text-center mb-1">
                If this was you, tap <strong>Approve</strong> to continue.
              </p>
              <p className="text-xs text-muted-foreground text-center mb-5">
                If you didn't try to log in, tap <strong>Deny</strong> to block it.
              </p>

              {/* Countdown */}
              <div className={`flex items-center justify-center gap-1.5 mb-5 ${urgentColour}`}>
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">{secondsLeft}s remaining</span>
              </div>

              {/* Countdown bar */}
              <div className="w-full bg-muted rounded-full h-1.5 mb-6">
                <div
                  className="h-1.5 rounded-full transition-all duration-1000"
                  style={{
                    width: `${(secondsLeft / totalSeconds.current) * 100}%`,
                    backgroundColor: secondsLeft <= 10 ? '#ef4444' : 'var(--color-primary)',
                  }}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                  disabled={responding}
                  onClick={() => respond('deny')}
                >
                  <ShieldX className="w-4 h-4 mr-1.5" />
                  Deny
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  disabled={responding}
                  onClick={() => respond('approve')}
                >
                  <ShieldCheck className="w-4 h-4 mr-1.5" />
                  Approve
                </Button>
              </div>
              {respondError && (
                <p className="text-xs text-red-500 text-center mt-3">{respondError}</p>
              )}
            </>
          )}

          {result === 'approved' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <ShieldCheck className="w-12 h-12 text-green-500" />
              <p className="text-sm font-semibold text-green-600">Login Approved</p>
              <p className="text-xs text-muted-foreground">You're all set.</p>
            </div>
          )}

          {result === 'denied' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <ShieldX className="w-12 h-12 text-red-500" />
              <p className="text-sm font-semibold text-red-600">Login Denied</p>
              <p className="text-xs text-muted-foreground">The login attempt has been blocked.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
